<script lang="ts" setup>
	import type { Key } from 'ant-design-vue/lib/vc-table/interface'
	import usePurchases from '~/services/apis/purchases-info'
	import useStaffActivation from '~/composables/useStaffActivation'
	import StringMethods from '~/utils/character'
	import type { RecordSchema } from '@/types'

	const props = defineProps<{
		purchaseId: string
		purchaseInfo: PlanInformationContext | null
	}>()
	const { getPolicies, fetchInactivePolicies, getDeactivatedPolicies } = usePurchases()
	const { urlQuery } = useUtils()
	const planInfo = computed(() => props.purchaseInfo)
	const { getTableRecordColumn, fetchPendingPolicies, categoryType, isPlanExpired } = useStaffActivation(planInfo, {
		purchase_id: props.purchaseId
	})
	const { formatDate } = useCustomDate()
	const loadingPolicy = shallowRef(false)
	const deactivateDialog = shallowRef(false)
	const searchVal = shallowRef('')
	const currentTab = shallowRef()

	const policyMetrics = shallowRef({
		pendingCount: planInfo.value?.staff_pending_policies_count ?? 0,
		activeCount: planInfo.value?.staff_policies_count ?? planInfo.value?.policies_count ?? 0,
		deactivatedCount: planInfo.value?.deactivated_policies_count ?? 0,
		queuedCount: planInfo.value?.staff_queued_pending_policies_count ?? 0
	})
	const activeTab = computed(() => {
		return (urlQuery.value?.status ?? 'pending') as 'queued' | 'pending' | 'active' | 'deactivated' | 'expired'
	})
	const tabList = computed<lineTabContext[]>(() => {
		const { toCapitalize } = StringMethods()
		const policyLabel = isPlanExpired.value ? 'expired' : 'active'
		return [
			{
				name: `Queued Policies (${policyMetrics.value.queuedCount})`,
				key: 'queued',
				disabled: !policyMetrics.value.queuedCount,
				content: 'These policies will be moved to pending policies on next plan cycle'
			},
			{
				name: `Pending Policies (${policyMetrics.value.pendingCount})`,
				key: 'pending',
				content: 'These policies will not be able to access care until activated'
			},
			{
				name: `${toCapitalize(policyLabel)} Policies (${policyMetrics.value.activeCount})`,
				key: `${policyLabel}`,
				content: !isPlanExpired.value
					? 'These policies are active and can access care at any time.'
					: 'These policies are expired and can not access care until renewed.'
			},
			{
				name: `Deactivated Policies (${policyMetrics.value.deactivatedCount})`,
				key: 'deactivated',
				content: 'These policies have been deactivated from your plan and will not be able to access care',
				disabled: !policyMetrics.value.deactivatedCount
			}
		]
	})

	const filterDetails = <T extends { key: string }>(arr: T[], value: string): T[] => {
		return arr.filter(item => item.key !== value) as T[]
	}
	const expiry_column: TableColumn = {
		key: 'expiry_date',
		dataIndex: 'expiry_date',
		title: 'Expiry Date'
	}
	const tableColumn = computed(() => {
		const source = getTableRecordColumn(props?.purchaseInfo ?? null, { at: 5, rowToAdd: expiry_column })
		const list = [...(source ?? [])] as TableColumn[]
		if (activeTab.value === 'queued') {
			return filterDetails(getTableRecordColumn(props?.purchaseInfo ?? null), 'details')
		}
		return list
	})
	const { addItems, findByStatus, clearStore, formatItems } = useMigration()
	const listOfRecordsIndex = ref<(string | number)[]>([])
	const listOfRecords = ref<string[]>([])
	const tableOptions = computed<TableFilterOptions>(() => ({
		cache: 0,
		filter: {
			search: searchVal.value
		},
		watch: [currentTab.value],
		...(!['deactivated', 'queued'].includes(activeTab.value)
			? {
					rowSelection: {
						selectedRowKeys: listOfRecordsIndex.value,
						onChange: onRowSelect
					}
				}
			: {})
	}))

	const editOptions = computed(() => [
		{
			name: `Change plan${listOfRecords.value.length > 1 ? 's' : ''}`,
			icon: {
				name: 'switch',
				dir: 'line'
			},
			onTrigger: async () => {
				if (listOfRecords.value.length > 0) {
					await navigateTo({
						name: 'mycover-plan_id-edit-plan',
						query: { page: 'plan_grades', migrationType: 'policies' },
						params: {
							plan_id: props.purchaseId
						}
					})
				}
			},
			disabled: !categoryType.value.isHealth
		},
		{
			name: 'Deactivate policy',
			icon: {
				name: 'trash',
				dir: 'other'
			},
			onTrigger: () => {
				deactivateDialog.value = true
			}
		}
	])
	const getCurrentTab = computed(() => tabList.value.find(tabs => tabs.key === activeTab.value) || null)
	const onRowSelect = async (index: Key[], list: EmployeesResponse[]) => {
		const item: DbItemType[] = formatItems(list, index)
		listOfRecords.value = list.map(item => item?.id ?? item?.temp_id ?? '')
		listOfRecordsIndex.value = index
		if (activeTab.value === 'pending') {
			await addItems('pending-policies', item, props.purchaseId)
		} else {
			await addItems('policies', item, props.purchaseId)
		}
	}

	// Fetch table records based on the active tab
	const getRecord = async (arg?: RecordSchema) => {
		return await resolve(
			async () => {
				if (['active', 'deactivated', 'expired'].includes(activeTab.value)) {
					if (activeTab.value === 'deactivated') {
						return await getDeactivatedPolicies({ purchase_id: String(props.purchaseId), ...arg })
					}
					const res = await getPolicies({
						purchase_id: String(props.purchaseId),
						is_active: activeTab.value === 'active',
						is_expired: activeTab.value === 'expired',
						...arg
					})
					return res ?? null
				} else if (!activeTab.value || activeTab.value === 'pending') {
					return await fetchPendingPolicies({ ...arg }, props.purchaseId)
				} else if (activeTab.value === 'queued') {
					return await fetchInactivePolicies({ purchase_id: props.purchaseId, ...arg })
				}
				return null
			},
			loadingPolicy,
			500
		)
	}
	const linkRoute = (arg: RecordSchema) => {
		if (activeTab.value === 'deactivated') {
			if (!arg?.meta?.policy_id) {
				return useNotification().alert({
					status: 404,
					message: 'Policy ID not found'
				})
			}
			return navigateTo({
				name: 'mycover-custodian-custodian_id',
				params: {
					custodian_id: arg.meta.policy_id
				}
			})
		} else if (activeTab.value === 'pending' || !activeTab.value) {
			return navigateTo({
				name: 'mycover-custodian-custodian_id',
				params: {
					custodian_id: arg.id
				},
				query: {
					status: 'pending'
				}
			})
		}
		navigateTo(`/mycover/custodian/${arg.id}`)
	}
	// Refresh table and clear selection
	const refreshTable = async () => {
		await refreshNuxtData('plan-details')
		listOfRecords.value = []
		listOfRecordsIndex.value = []
		await Promise.all([
			clearStore('pending-policies', planInfo.value?.id ?? ''),
			clearStore('policies', planInfo.value?.id ?? '')
		])
	}

	const toggleStatus = async (e: string = activeTab.value) => {
		let result: DbItemType[] | [] = []
		listOfRecords.value = []
		listOfRecordsIndex.value = []
		await findByStatus(
			e as 'active' | 'pending',
			res => {
				result = res
			},
			props.purchaseId
		)
		listOfRecords.value = result.map(item => item.record.id)
		listOfRecordsIndex.value = result.map(item => item.indexNumber)
	}
	onMounted(toggleStatus)

	async function onTabChange(e: string) {
		await toggleStatus(e)
		currentTab.value = e
	}
</script>

<template>
	<section>
		<div class="mt-5 flex flex-wrap items-start justify-between gap-y-5">
			<div class="overflow-hidden">
				<tabs-pill
					:tabs="tabList"
					active-tab="pending"
					query-key="status"
					:ui="{
						wrapper: {
							height: 'h-[54px]',
							rounded: 'rounded-xl',
							default: 'border overflow-x-auto flex w-full'
						},
						tab: {
							active: {
								color: [
									'text-white',
									{
										'bg-main': activeTab === 'active',
										'bg-error-500': activeTab === 'expired',
										'bg-warning-500':
											activeTab === 'pending' || activeTab === 'queued' || !activeTab,
										'bg-gray-500': activeTab === 'deactivated'
									}
								],
								font: 'font-[600]'
							},
							inactive: {
								color: 'text-gray-500',
								font: 'font-medium'
							},
							base: 'text-sm  whitespace-nowrap',
							rounded: '',
							width: 'w-full',
							border: 'border-r last:border-r-0'
						}
					}"
					use-url
					@change="onTabChange"
				/>
				<span class="text-sm font-medium text-gray-700">
					{{ getCurrentTab?.content ?? '' }}
				</span>
			</div>
			<forms-search-field v-model:value="searchVal" class="w-full max-w-[380px] bg-white" />
		</div>

		<div class="mt-5 sm:mt-14">
			<div v-show="listOfRecordsIndex.length > 0" class="mb-8 flex items-center gap-x-3 overflow-x-auto">
				<button
					v-for="(edit, i) in editOptions"
					:key="'edit-option-' + i"
					type="button"
					class="font-xs inline-flex items-center gap-x-2 rounded-[20px] border-[0.8px] border-gray-400 bg-gray-100 px-[12px] py-[4px] font-medium text-gray-700"
					:class="{ 'inline-flex': !edit?.disabled, hidden: edit?.disabled }"
					:name="edit.name"
					@click="edit.onTrigger"
				>
					<lazy-tools-icon :icon="edit.icon.name" :dir="edit.icon.dir" :width="16" :height="16" />
					<span>{{ edit.name }}</span>
				</button>
			</div>

			<tables-policies
				:columns="tableColumn"
				:records="getRecord"
				:loading="loadingPolicy"
				:table-config="tableOptions"
				:ui="{ table: '' }"
			>
				<template #expiry_date="{ record }">
					<span v-if="categoryType.isHealth">
						{{
							planInfo?.actual_renewal_date
								? formatDate(planInfo.actual_renewal_date, 'DD MMM, YYYY')
								: (planInfo?.renewal_date && formatDate(planInfo?.renewal_date, 'DD MMM, YYYY')) ||
									'N/A'
						}}
					</span>
					<span v-else>
						{{
							record?.policy_expiration_date
								? formatDate(record?.policy_expiration_date, 'DD MMM, YYYY')
								: formatDate(purchaseInfo?.renewal_date ?? '')
						}}
					</span>
				</template>
				<template #policy_type="{ record }">
					<span>
						{{
							record?.purchase?.details?.health_plan_type ??
							record?.purchase?.meta?.health_plan_type ??
							HealthType.STANDARD
						}}
					</span>
				</template>
				<template #details="{ record }">
					<button
						type="button"
						aria-label="activate staff"
						aria-roledescription="view beneficiary plan details"
						class="text-sm font-medium"
						@click="linkRoute(record)"
					>
						<span class="font-medium text-primary-2"> View </span>
					</button>
				</template>
			</tables-policies>
		</div>

		<employees-deactivate-policy-modal
			v-model="deactivateDialog"
			v-model:selected-ids="listOfRecords"
			:product-category="purchaseInfo?.product?.productCategory?.name ?? ''"
			:product-code="purchaseInfo?.product?.code_name ?? 0"
			:status="activeTab"
			with-policy-id
			@submitted="() => refreshTable()"
		/>
	</section>
</template>
